/**
 * Speech-to-Text Service using Web Speech API
 * Completely free, real-time speech recognition
 */

export interface STTOptions {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  maxAlternatives?: number;
}

export interface RecognitionResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
}

export class STTService {
  private recognition: SpeechRecognition | null = null;
  private isListening: boolean = false;
  private onResultCallback: ((result: RecognitionResult) => void) | null = null;
  private onErrorCallback: ((error: string) => void) | null = null;
  private onEndCallback: (() => void) | null = null;
  private audioContext: AudioContext | null = null;
  private timeoutId: number | null = null; // Timeout for auto-stop
  private maxRecordingTime: number = 10000; // Max 10 seconds per recording

  constructor() {
    this.initRecognition();
  }

  // Initialize speech recognition
  private initRecognition(): void {
    // Check browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('⚠️ Speech recognition not supported in this browser');
      return;
    }

    this.recognition = new SpeechRecognition();

    // Default settings for kids
    this.recognition.lang = 'en-US';
    this.recognition.continuous = false; // Single utterance mode
    this.recognition.interimResults = true; // Show results while speaking
    this.recognition.maxAlternatives = 3; // Get multiple options

    console.log('✅ Speech recognition initialized');
  }

  // Request microphone permission explicitly
  async requestMicrophonePermission(): Promise<boolean> {
    try {
      console.log('🎤 Requesting microphone permission...');

      // Request permission using getUserMedia
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create audio context to test microphone access
      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.audioContext.destination);

      // Stop the stream immediately (we just wanted to test permission)
      stream.getTracks().forEach(track => track.stop());

      console.log('✅ Microphone permission granted');
      return true;
    } catch (error: any) {
      console.error('❌ Microphone permission denied:', error);

      if (error.name === 'NotAllowedError') {
        alert('请允许麦克风权限才能进行录音练习。\n\n在浏览器地址栏左侧点击图标，允许麦克风访问。');
      } else if (error.name === 'NotFoundError') {
        alert('未检测到麦克风设备。\n\n请检查麦克风是否正确连接。');
      } else {
        alert('无法访问麦克风：' + error.message);
      }

      return false;
    }
  }

  // Set language
  setLanguage(lang: string): void {
    if (this.recognition) {
      this.recognition.lang = lang;
    }
  }

  // Set callbacks
  onResult(callback: (result: RecognitionResult) => void): void {
    this.onResultCallback = callback;
  }

  onError(callback: (error: string) => void): void {
    this.onErrorCallback = callback;
  }

  onEnd(callback: () => void): void {
    this.onEndCallback = callback;
  }

  // Start listening with permission check
  async start(options?: STTOptions): Promise<void> {
    // First check permission
    const hasPermission = await this.requestMicrophonePermission();
    if (!hasPermission) {
      throw new Error('麦克风权限被拒绝');
    }

    return new Promise((resolve, reject) => {
      if (!this.recognition) {
        reject(new Error('Speech recognition not supported'));
        return;
      }

      // Apply options
      if (options?.lang) this.recognition.lang = options.lang;
      if (options?.continuous !== undefined) this.recognition.continuous = options.continuous;
      if (options?.interimResults !== undefined) this.recognition.interimResults = options.interimResults;
      if (options?.maxAlternatives) this.recognition.maxAlternatives = options.maxAlternatives;

      // Set up event handlers
      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        const result = event.results[event.results.length - 1];
        const transcript = result[0].transcript;
        const confidence = result[0].confidence;

        console.log(`🎯 Speech result: "${transcript}" (confidence: ${confidence.toFixed(2)}, final: ${result.isFinal})`);

        if (this.onResultCallback) {
          this.onResultCallback({
            transcript,
            confidence,
            isFinal: result.isFinal,
          });
        }

        // Resolve when we get a final result
        if (result.isFinal) {
          this.clearTimeout();
          resolve();
        }
      };

      this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('❌ Speech recognition error:', event.error);
        const errorMessage = this.getErrorMessage(event.error);
        if (this.onErrorCallback) {
          this.onErrorCallback(errorMessage);
        }
        reject(new Error(errorMessage));
      };

      this.recognition.onend = () => {
        console.log('⏹️ Speech recognition ended');
        this.isListening = false;
        this.clearTimeout();
        if (this.onEndCallback) {
          this.onEndCallback();
        }
      };

      // Start recognition with timeout
      try {
        this.isListening = true;
        this.recognition.start();
        console.log('🎤 Speech recognition started');

        // Set timeout to auto-stop if no result
        this.timeoutId = window.setTimeout(() => {
          console.log('⏰ Recording timeout, stopping...');
          if (this.isListening) {
            this.stop();
            if (this.onErrorCallback) {
              this.onErrorCallback('录音超时，请再试一次。请尽早开始说话。');
            }
            reject(new Error('录音超时'));
          }
        }, this.maxRecordingTime);
      } catch (error: any) {
        console.error('❌ Failed to start recognition:', error);
        reject(new Error('启动语音识别失败：' + error.message));
      }
    });
  }

  // Stop listening
  stop(): void {
    this.clearTimeout();
    if (this.recognition && this.isListening) {
      this.recognition.stop();
      this.isListening = false;
      console.log('⏹️ Speech recognition stopped');
    }
  }

  // Clear timeout
  private clearTimeout(): void {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  // Check if listening
  isCurrentlyListening(): boolean {
    return this.isListening;
  }

  // Get error message
  private getErrorMessage(error: string): string {
    const errorMessages: Record<string, string> = {
      'no-speech': '没有检测到语音，请再试一次。请确保对着麦克风说话。',
      'audio-capture': '无法访问麦克风，请检查设备。',
      'not-allowed': '麦克风权限被拒绝。请在浏览器设置中允许麦克风访问。',
      'network': '网络错误，语音识别需要网络连接。',
      'aborted': '录音被取消。',
      'language-not-supported': '不支持的语言。',
      'service-not-allowed': '语音识别服务不可用。',
    };

    return errorMessages[error] || `识别错误: ${error}`;
  }

  // Quick listen for single word/phrase
  async listenForPhrase(): Promise<string> {
    return new Promise((resolve, reject) => {
      // Set up result callback first
      this.onResult((result) => {
        if (result.isFinal) {
          resolve(result.transcript);
        }
      });

      // Start listening
      this.start({ continuous: false, interimResults: false })
        .catch(reject);
    });
  }

  // Check browser support
  isSupported(): boolean {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    return !!SpeechRecognition;
  }

  // Get supported status message
  getSupportMessage(): string {
    if (this.isSupported()) {
      return '✅ 支持语音识别';
    }
    return '⚠️ 当前浏览器不支持语音识别。请使用Chrome、Safari或Edge浏览器。';
  }
}

// Create singleton instance
export const sttService = new STTService();